import { Button, CircularProgress } from '@material-ui/core';
import { Additional, FrontLayout, Terms } from 'components';
import { useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import SizeCard from 'shared/components/SizeCard/SizeCard';
import { COLORS } from 'shared/constants/COLORS';
import { ROUTES } from 'shared/constants/ROUTES';
import { CustomTypography } from 'shared/elements/CustomTypography';
import { checkInService } from 'shared/services/checkInService';
import { projectService } from 'shared/services/projectService';
import { tokenService } from 'shared/services/tokenService';
import { ls } from 'shared/utils/ls';
import { BlackCont } from './Unauthenticated.styles';
import Verification from './Verification/Verification';

export enum EVerificationStep {
  SIZES = 'sizes',
  QUESTIONS = 'questions',
  NDA = 'nda',
  COMPLETE = 'complete',
}
const { getCheckInDetails } = checkInService();
const { getSingleProjectGQL } = projectService();

const Unauthenticated = () => {
  const { step } = useParams() as { step: EVerificationStep };
  const { getLS, clearLS, setLS } = ls();
  const authToken = getLS('auth_token');
  const projectName = getLS('project_name');
  const projectId = getLS('projectId');
  const roleId = getLS('role_id');
  const projectRoles = getLS('project_roles');
  const history = useHistory();

  const { data, isLoading: isProjectLoading } = getSingleProjectGQL(projectId);

  const pData = data?.data.project;

  const sessionId = pData?.ongoing_session?.id || '';

  const ndaRequired = pData?.ongoing_session?.required_signed_nda || false;

  const { data: checkinData, isLoading: isCheckinLoading } = getCheckInDetails(authToken ? sessionId : '');

  const auditionId = useMemo(() => {
    if (isCheckinLoading) return '';
    return checkinData?.data?.id || '';
  }, [checkinData, isCheckinLoading]);

  const handleNext = () => {
    switch (step) {
      case 'sizes':
        return history.push(`${ROUTES.UNAUTHENTICATED}/${EVerificationStep.QUESTIONS}`);
      case 'questions':
        return ndaRequired
          ? history.push(`${ROUTES.UNAUTHENTICATED}/${EVerificationStep.NDA}`)
          : history.push(`${ROUTES.UNAUTHENTICATED}/${EVerificationStep.COMPLETE}`);
      case 'nda':
        return history.push(`${ROUTES.UNAUTHENTICATED}/${EVerificationStep.COMPLETE}`);
      case 'complete':
        return history.push(ROUTES.ROOT);
      default:
        return history.push(`${ROUTES.UNAUTHENTICATED}/${EVerificationStep.SIZES}`);
    }
  };

  const renderHeader = () => {
    switch (step) {
      case 'sizes':
        return 'Please check your sizes below';
      case 'questions':
        return 'Please answer the following questions';
      case 'nda':
        return 'Please read and sign the following NDA/Terms';
      case 'complete':
        return 'Check-in Complete!';
      default:
        return ` Verification for ${projectName} Audition`;
    }
  };

  const renderBody = () => {
    if (tokenService().isAuthenticated()) return <Verification />;

    if (!auditionId && !isCheckinLoading)
      return (
        <CustomTypography style={{ textAlign: 'center' }} fontSize={18}>
          No Audition Data
        </CustomTypography>
      );
    if (!sessionId && !isCheckinLoading)
      return (
        <CustomTypography style={{ textAlign: 'center' }} fontSize={18}>
          No Active Session Found
        </CustomTypography>
      );

    if (isProjectLoading || isCheckinLoading)
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
            alignItems: 'center',
          }}
        >
          <CircularProgress style={{ alignSelf: 'center' }} />
        </div>
      );
    switch (step) {
      case 'sizes':
        return <SizeCard onSave={handleNext} checkinSelfTapes />;
      case 'questions':
        return (
          <Additional
            roleId={roleId}
            auditionID={auditionId}
            handleNext={handleNext}
            handlePrev={() => history.push(`${ROUTES.UNAUTHENTICATED}/${EVerificationStep.SIZES}`)}
            projectId={projectId}
          />
        );
      case 'nda':
        return (
          <Terms
            nda={pData?.ongoing_session.nda_content}
            ndaRequired={pData?.ongoing_session.required_signed_nda}
            data={checkinData?.data}
            handleNext={handleNext}
            handlePrev={() => history.push(`${ROUTES.UNAUTHENTICATED}/${EVerificationStep.QUESTIONS}`)}
            sessionId={pData && pData?.ongoing_session.id ? pData?.ongoing_session.id : ''}
          />
        );
      case 'complete':
        clearLS();

        return (
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CustomTypography style={{ textAlign: 'center', fontSize: 20 }}>
              Thank you for checking in, please wait to be called in for your audition.
            </CustomTypography>
            <Button
              onClick={() => {
                setLS('project_name', projectName);
                setLS('project_roles', projectRoles);
                setLS('projectId', projectId);
                setLS('option', 'check-in');
                setTimeout(() => {
                  history.push('/login');
                }, 100);
              }}
              style={{
                background: COLORS.PRIMARY,
                marginTop: 30,
                height: 50,
                width: 'fit-content',
                alignSelf: 'center',
                padding: '0 50px',
                minWidth: 300,
                transition: 'opacity 0.3s',
              }}
            >
              <CustomTypography fontSize={16} fontWeight={700} color="white">
                Finish
              </CustomTypography>
            </Button>
          </div>
        );
      default:
        return <Verification />;
    }
  };

  return (
    <FrontLayout type="new" containerWidth={'100%'} noImage>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          alignItems: 'center',
        }}
      >
        <CustomTypography fontSize={22} style={{ textAlign: 'center' }}>
          {renderHeader()}
        </CustomTypography>
        <BlackCont
          style={{
            ...(!step ? { width: '50%' } : { width: '70%' }),
          }}
        >
          {renderBody()}
        </BlackCont>
      </div>
    </FrontLayout>
  );
};

export default Unauthenticated;
